// Loads YouTube's official IFrame Player API once and shares it across every
// embed on the page. Hand-rolled postMessage parsing (listening for raw
// "infoDelivery" events) turned out unreliable — YouTube's player only
// starts broadcasting those once it's registered via this API's own
// handshake, which the API script itself handles correctly. Using the real
// YT.Player wrapper (bound to our own already-rendered <iframe>, not a
// replacement) gives a proper onStateChange event and a real pauseVideo().

export interface YTPlayerInstance {
  pauseVideo: () => void;
  destroy: () => void;
}

interface YTPlayerOptions {
  events: {
    onStateChange?: (event: { data: number }) => void;
  };
}

interface YTNamespace {
  Player: new (el: HTMLElement, options: YTPlayerOptions) => YTPlayerInstance;
  PlayerState: { PLAYING: number };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YTNamespace> | null = null;

export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve(window.YT!);
    };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    }
  });

  return apiPromise;
}
